/**
 * Daten-Laden, Typen und Ableitungen (§5 / §8 des Plans).
 *
 * Das Frontend programmiert ausschließlich gegen die Daten-Verträge in §5.
 * Quelle ist zunächst statisch `public/data/`, später per einzelner Konstante
 * `DATA_BASE_URL` auf eine API-URL umschaltbar — sonst ändert sich nichts.
 */

import { KlassenKey, Verteilung } from './klassen';

/**
 * Basis-URL der Daten. Leer = relativ zu `public/` (statische Dateien).
 * Für ein echtes Backend hier z. B. "https://api.example.com/regionen" setzen;
 * die Dateinamen (bundeslaender.geojson usw.) bleiben gleich.
 */
export const DATA_BASE_URL = '';

/**
 * Projektion der ausgelieferten GeoJSON-Daten. Der Konverter
 * (build_frontend_data.py) schreibt bereits Web Mercator (EPSG:3857) — dieselbe
 * Projektion, in der OpenLayers rendert — sodass im Browser keine Reprojektion
 * mehr nötig ist. Ein Backend, das EPSG:4326 liefert, würde hier 'EPSG:4326'
 * setzen (OpenLayers transformiert dann beim Einlesen).
 */
export const DATA_PROJECTION = 'EPSG:3857';

// ---------------------------------------------------------------------------
// GeoJSON-Typen (bewusst minimal gehalten — kein @types/geojson nötig)
// ---------------------------------------------------------------------------

export interface RegionProperties {
  id: string;
  name?: string;
  /** Klassenverteilung als Flächen-% (alle Ebenen). */
  verteilung?: Verteilung;
  /** Klassenverteilung in Hektar (aus der Datenquelle). */
  verteilungHa?: Verteilung;
  /** Gesamtfläche der Region in Hektar. */
  flaecheHa?: number;
  /** nur Landkreis: Fremdschlüssel auf bundesland.id */
  bundeslandId?: string;
  /** nur Feld: Fremdschlüssel auf landkreis.id */
  landkreisId?: string;
  [key: string]: unknown;
}

export interface RegionGeometry {
  type: string;
  coordinates: unknown;
}

export interface RegionFeature {
  type: 'Feature';
  properties: RegionProperties;
  geometry: RegionGeometry;
}

export interface RegionCollection {
  type: 'FeatureCollection';
  features: RegionFeature[];
}

const EMPTY: RegionCollection = { type: 'FeatureCollection', features: [] };

// ---------------------------------------------------------------------------
// Region-Info (§5.4) — optional, entkoppelt von der Geometrie
// ---------------------------------------------------------------------------

export interface Kennzahl {
  label: string;
  wert: number;
  einheit: string;
}

export interface RegionInfoEntry {
  kennzahlen?: Kennzahl[];
  beschreibung?: string;
}

export type RegionInfoMap = Record<string, RegionInfoEntry>;

// ---------------------------------------------------------------------------
// Laden
// ---------------------------------------------------------------------------

// Datenpfad → URL. Keine manuelle Cache-Version: nginx liefert /data/ mit
// `expires -1` (ETag-Revalidierung) aus, sodass ein Datenupdate (data-prep)
// automatisch erkannt wird — kein alter, gecachter GeoJSON-Stand (s.
// nginx/default.conf).
function url(path: string): string {
  const base = DATA_BASE_URL.replace(/\/$/, '');
  return base ? `${base}/${path}` : `${process.env.PUBLIC_URL || ''}/data/${path}`;
}

async function fetchJson<T>(path: string): Promise<T | null> {
  try {
    const res = await fetch(url(path));
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

export async function loadBundeslaender(): Promise<RegionCollection> {
  const data = await fetchJson<RegionCollection>('bundeslaender.geojson');
  return data ?? EMPTY;
}

export async function loadLandkreise(): Promise<RegionCollection> {
  const data = await fetchJson<RegionCollection>('landkreise.geojson');
  return data ?? EMPTY;
}

export async function loadRegionInfo(): Promise<RegionInfoMap> {
  const data = await fetchJson<RegionInfoMap>('regionInfo.json');
  return data ?? {};
}

// --- Felder: bedarfsgerecht, mit Cache (§5.3 / §7.2) ---

const felderCache = new Map<string, RegionCollection>();
let felderGesamtPromise: Promise<RegionCollection> | null = null;

/**
 * Lädt die Felder eines Landkreises bedarfsgerecht.
 * Unterstützt BEIDE Vertragsformen (§5.3):
 *   1) partitioniert:  felder/<landkreisId>.geojson  (bevorzugt)
 *   2) eine Datei:     felder.geojson  (wird einmal geladen und gefiltert)
 * Ergebnis wird je Landkreis gecacht.
 */
export async function loadFelderForLandkreis(landkreisId: string): Promise<RegionCollection> {
  const cached = felderCache.get(landkreisId);
  if (cached) return cached;

  // 1) partitionierte Datei versuchen
  const partitioned = await fetchJson<RegionCollection>(`felder/${landkreisId}.geojson`);
  if (partitioned && partitioned.features) {
    felderCache.set(landkreisId, partitioned);
    return partitioned;
  }

  // 2) Fallback: eine große felder.geojson einmal laden und filtern
  if (!felderGesamtPromise) {
    felderGesamtPromise = fetchJson<RegionCollection>('felder.geojson').then((d) => d ?? EMPTY);
  }
  const gesamt = await felderGesamtPromise;
  const gefiltert: RegionCollection = {
    type: 'FeatureCollection',
    features: gesamt.features.filter((f) => f.properties.landkreisId === landkreisId),
  };
  felderCache.set(landkreisId, gefiltert);
  return gefiltert;
}

// ---------------------------------------------------------------------------
// Ableitungen für die Sidebar
// ---------------------------------------------------------------------------

export interface RegionListItem {
  id: string;
  name: string;
  verteilung?: Verteilung;
  verteilungHa?: Verteilung;
  flaecheHa?: number;
}

/** Bundesländer als Liste (Reihenfolge wie in der Datei: HE, RP). */
export function bundeslaenderListe(coll: RegionCollection): RegionListItem[] {
  return coll.features.map((f) => ({
    id: f.properties.id,
    name: f.properties.name ?? f.properties.id,
    verteilung: f.properties.verteilung,
    verteilungHa: f.properties.verteilungHa,
    flaecheHa: f.properties.flaecheHa,
  }));
}

/** Landkreise eines Bundeslandes, alphabetisch nach Name sortiert (§7.1). */
export function landkreiseFuerBundesland(
  coll: RegionCollection,
  bundeslandId: string
): RegionListItem[] {
  return coll.features
    .filter((f) => f.properties.bundeslandId === bundeslandId)
    .map((f) => ({
      id: f.properties.id,
      name: f.properties.name ?? f.properties.id,
      verteilung: f.properties.verteilung,
      verteilungHa: f.properties.verteilungHa,
      flaecheHa: f.properties.flaecheHa,
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'de'));
}

/** Findet die ha-Verteilung einer Region über alle geladenen Ebenen. */
export function findeVerteilungHa(
  id: string,
  ...collections: (RegionCollection | undefined)[]
): Verteilung | undefined {
  for (const coll of collections) {
    if (!coll) continue;
    const f = coll.features.find((feat) => feat.properties.id === id);
    if (f) return f.properties.verteilungHa;
  }
  return undefined;
}

export type { KlassenKey };
